import React, { useState } from 'react';
import PageTitle from '../components/common/PageTitle';
import { Phone, Mail, MapPin, Clock, ArrowRight, CheckCircle } from 'lucide-react';

const KontaktPage = () => {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('submitting');
    
    // Simulate form submission
    setTimeout(() => {
      // In a real application, you would submit the form data to your backend here
      console.log('Form submitted:', formData);
      setFormStatus('success');
      
      // Reset form after successful submission
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      });
      
      // Reset status after 5 seconds
      setTimeout(() => {
        setFormStatus('idle');
      }, 5000);
    }, 1500);
  };

  return (
    <>
      <PageTitle 
        title="Kontakt" 
        subtitle="Stopite v stik z nami za več informacij"
        backgroundImage="/images/contact-hero.jpg"
      />

      <section className="page-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2 className="text-2xl font-bold text-secondary mb-6">Kontaktne informacije</h2>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="bg-primary/10 p-3 rounded-full mr-4">
                    <MapPin className="text-primary h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-secondary">Naslov</h3>
                    <p className="text-gray-600">Gasilski dom Zgornja Polskava</p>
                    <p className="text-gray-600">Mladinska ulica 12</p>
                    <p className="text-gray-600">2314 Zgornja Polskava</p>
                    <a 
                      href="https://maps.google.com/?q=Mladinska+ulica+12,+2314+Zgornja+Polskava,+Slovenia" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-primary hover:text-primary-700 text-sm inline-flex items-center mt-1"
                    >
                      Odpri v Google Maps
                      <ArrowRight className="h-3 w-3 ml-1" />
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-primary/10 p-3 rounded-full mr-4">
                    <Phone className="text-primary h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-secondary">Telefon</h3>
                    <p className="text-gray-600">Tajništvo: <a href="tel:+38627525020" className="text-primary hover:text-primary-700">+386 2 752 50 20</a></p>
                    <p className="text-gray-600">Predsednik: <a href="tel:+38641123456" className="text-primary hover:text-primary-700">+386 41 123 456</a></p>
                    <p className="text-gray-600">Poveljnik: <a href="tel:+38651987654" className="text-primary hover:text-primary-700">+386 51 987 654</a></p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-primary/10 p-3 rounded-full mr-4">
                    <Mail className="text-primary h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-secondary">Email</h3>
                    <p className="text-gray-600"><a href="mailto:info@pgd-zgornja-polskava.si" className="text-primary hover:text-primary-700">info@pgd-zgornja-polskava.si</a></p>
                    <p className="text-gray-600"><a href="mailto:predsednik@pgd-zgornja-polskava.si" className="text-primary hover:text-primary-700">predsednik@pgd-zgornja-polskava.si</a></p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-primary/10 p-3 rounded-full mr-4">
                    <Clock className="text-primary h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-secondary">Uradne ure</h3>
                    <p className="text-gray-600">Ponedeljek: 18:00 - 20:00</p>
                    <p className="text-gray-600">Sreda: 18:00 - 20:00</p>
                    <p className="text-gray-600">Za nujne primere smo dosegljivi 24/7 na številki <strong>112</strong></p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Contact Form */}
            <div className="lg:col-span-2 bg-white p-6 md:p-8 rounded-xl shadow-md">
              <h2 className="text-2xl font-bold text-secondary mb-6">Pošljite nam sporočilo</h2>
              
              {formStatus === 'success' ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-green-800 mb-2">Sporočilo poslano!</h3>
                  <p className="text-green-700">
                    Zahvaljujemo se vam za vaše sporočilo. Odgovorili vam bomo v najkrajšem možnem času.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                        Ime in priimek <span className="text-primary">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                        placeholder="Vnesite vaše ime in priimek"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email <span className="text-primary">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                        placeholder="vase.ime@primer.si"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                        Telefon
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                        placeholder="041 123 456"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                        Zadeva <span className="text-primary">*</span>
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                      >
                        <option value="">Izberite zadevo</option>
                        <option value="Splošno vprašanje">Splošno vprašanje</option>
                        <option value="Članstvo">Članstvo</option>
                        <option value="Dogodki">Dogodki</option>
                        <option value="Donacija">Donacija</option>
                        <option value="Drugo">Drugo</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                      Sporočilo <span className="text-primary">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-primary focus:border-primary"
                      placeholder="Vnesite vaše sporočilo..."
                    ></textarea>
                  </div>
                  
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="btn-primary"
                    >
                      {formStatus === 'submitting' ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Pošiljanje...
                        </>
                      ) : (
                        'Pošlji sporočilo'
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-10">
        <div className="container-custom">
          <div className="rounded-xl overflow-hidden shadow-md h-[400px]">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2742.6848777768874!2d15.57289067687854!3d46.54013937111693!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x476f77c48056193b%3A0x3c9df7b58d9ce195!2sGasilski%20dom%20PGD%20Slovenska%20Bistrica!5e0!3m2!1ssl!2ssi!4v1712676850870!5m2!1ssl!2ssi"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Lokacija gasilskega doma"
            ></iframe>
          </div>
        </div>
      </section>

      {/* Membership CTA Section */}
      <section className="page-section bg-gray-50">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="section-title text-secondary">Pridruži se nam</h2>
              <p className="mt-6 text-gray-700">
                PGD Zgornja Polskava vabi vse zainteresirane k včlanitvi v gasilsko društvo. 
                Z članstvom v našem društvu postanete del ekipe, ki skrbi za varnost lokalne skupnosti.
              </p>
              <p className="mt-4 text-gray-700">
                Ponujamo različne možnosti članstva:
              </p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-center">
                  <CheckCircle className="text-primary h-5 w-5 mr-2" />
                  <span>Operativni člani (aktivno sodelovanje pri intervencijah)</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="text-primary h-5 w-5 mr-2" />
                  <span>Podporni člani (pomoč pri organizaciji dogodkov)</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="text-primary h-5 w-5 mr-2" />
                  <span>Veterani (člani z dolgoletnimi izkušnjami)</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="text-primary h-5 w-5 mr-2" />
                  <span>Mladina (mladi gasilci od 6. leta naprej)</span>
                </li>
              </ul>
              
              <div className="mt-8">
                <a href="#contact-form" className="btn-primary">
                  Kontaktiraj nas za včlanitev
                </a>
              </div>
            </div>
            
            <div>
              <img 
                src="/images/join-us.jpg" 
                alt="Pridruži se nam" 
                className="rounded-xl shadow-lg w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Emergency Info Box */}
      <section className="py-6">
        <div className="container-custom">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="flex items-center mb-4 md:mb-0">
                <div className="bg-primary text-white p-3 rounded-full mr-4">
                  <Phone className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-secondary">Za nujne primere</h3>
                  <p className="text-gray-700">V primeru požara, nesreče ali druge nevarnosti</p>
                </div>
              </div>
              <div className="flex items-center">
                <span className="text-3xl font-bold text-primary mr-4">112</span>
                <a href="tel:112" className="btn-primary">
                  Pokliči 112
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default KontaktPage;